import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ImageUploader } from "@/components/ImageUploader";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Store, X, KeyRound, Copy, Power, ExternalLink } from "lucide-react";

const BLANK = {
  brand_name: "", legal_name: "", logo: "", description: "", website: "",
  contact_person: "", contact_email: "", contact_phone: "", address: "",
  operating_cities: [], product_genres: [], product_types: [],
  gst: "", pan: "", fssai: "", licenses: [], certifications: [], portal_email: "",
};

function TagInput({ value = [], onChange, placeholder, testid }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (v && !value.includes(v)) onChange([...value, v]);
    setDraft("");
  };
  return (
    <div>
      <div className="flex gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }}
          placeholder={placeholder} className="rounded-xl bg-white" data-testid={testid} />
        <Button type="button" variant="outline" onClick={add} className="rounded-xl border-sage shrink-0">Add</Button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {value.map((t) => (
            <span key={t} className="inline-flex items-center gap-1 bg-sage/20 text-forest text-xs px-2 py-1 rounded-full">
              {t}<button type="button" onClick={() => onChange(value.filter((x) => x !== t))}><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function CredentialsBox({ creds }) {
  const copy = (v, msg) => { navigator.clipboard.writeText(v); toast.success(msg); };
  return (
    <div className="bg-sage/10 border border-sage/40 rounded-2xl p-4 space-y-2" data-testid="brand-credentials">
      <p className="text-sm font-500 text-forest flex items-center gap-2"><KeyRound className="w-4 h-4" /> Brand Portal Login</p>
      <p className="text-xs text-brown/70">Email sending is managed in the Admin Panel — share these credentials with the brand manually.</p>
      {[["Portal Email", creds.portal_email], ["Temporary Password", creds.temp_password], ["Login Link", creds.login_link]].map(([k, v]) => (
        <div key={k} className="flex items-center justify-between gap-2 bg-white rounded-xl px-3 py-2">
          <div className="min-w-0"><p className="text-[10px] uppercase tracking-wide text-olive">{k}</p><p className="text-sm text-forest truncate">{v}</p></div>
          <button type="button" onClick={() => copy(v, `${k} copied`)} className="shrink-0 text-olive hover:text-forest" data-testid={`copy-${k.split(" ")[0].toLowerCase()}`}><Copy className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
  );
}

export default function AdminBrands() {
  const nav = useNavigate();
  const [list, setList] = useState(null);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creds, setCreds] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/brands").then((r) => setList(r.data));
  useEffect(() => { load(); api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  const toggleGenre = (name) => setEditing((cur) => ({
    ...cur,
    product_genres: cur.product_genres.includes(name) ? cur.product_genres.filter((g) => g !== name) : [...cur.product_genres, name],
  }));

  const save = async () => {
    if (!editing.brand_name.trim()) return toast.error("Brand name is required");
    if (!editing.id && !(editing.portal_email || editing.contact_email)) return toast.error("A contact or portal email is required to create the login");
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/admin/brands/${editing.id}`, editing);
        toast.success("Brand updated");
        setEditing(null); load();
      } else {
        const { data } = await api.post("/admin/brands", editing);
        toast.success("Brand created & portal login generated");
        setEditing(null); load();
        setCreds({ portal_email: data.portal_email, temp_password: data.temp_password, login_link: data.login_link, brand_name: data.brand_name });
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const del = (b) => { if (!window.confirm(`Delete brand "${b.brand_name}" and its portal login?`)) return; api.delete(`/admin/brands/${b.id}`).then(() => { toast.success("Brand deleted"); load(); }); };
  const toggleStatus = (b) => api.put(`/admin/brands/${b.id}/status`).then((r) => { toast.success(`Status: ${r.data.status}`); load(); });
  const regen = (b) => { if (!window.confirm("Generate a new temporary password? The old one stops working.")) return; api.post(`/admin/brands/${b.id}/regenerate-password`).then((r) => { setCreds({ ...r.data, brand_name: b.brand_name }); toast.success("New password generated"); load(); }); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl lg:text-4xl text-forest tracking-tight">Brands</h1>
          <p className="text-sm text-brown/70 mt-1">Onboard brand partners and manage their portal access.</p>
        </div>
        <Button onClick={() => setEditing({ ...BLANK })} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="add-brand-btn"><Plus className="w-4 h-4 mr-1" /> Add Brand</Button>
      </div>

      {!list ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-forest" /></div> :
        list.length === 0 ? <div className="text-center py-16 text-muted-foreground"><Store className="w-10 h-10 mx-auto mb-2 text-sage" />No brands yet. Add your first brand partner.</div> : (
        <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="brands-table">
              <thead className="bg-sand text-brown/70 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3">Brand</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-center px-4 py-3">Products</th>
                  <th className="text-center px-4 py-3">Pending Actions</th>
                  <th className="text-right px-4 py-3">Manage</th>
                </tr>
              </thead>
              <tbody>
                {list.map((b) => (
                  <tr key={b.id} className="border-t border-sage/15" data-testid={`brand-row-${b.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-sand overflow-hidden shrink-0 flex items-center justify-center">{b.logo ? <img src={b.logo} alt={b.brand_name} className="w-full h-full object-cover" /> : <Store className="w-4 h-4 text-sage" />}</div>
                        <div className="min-w-0"><button onClick={() => nav(`/admin/brands/${b.id}`)} data-testid={`open-brand-${b.id}`} className="font-500 text-forest truncate hover:underline text-left">{b.brand_name}</button><p className="text-xs text-brown/60 truncate">{b.portal_email}</p></div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={b.status === "active" ? "bg-sage/25 text-forest hover:bg-sage/25" : "bg-terracotta/15 text-terracotta hover:bg-terracotta/15"} data-testid={`brand-status-${b.id}`}>{b.status}</Badge>
                    </td>
                    <td className="px-4 py-3 text-center text-forest">{b.total_products ?? 0}</td>
                    <td className="px-4 py-3 text-center">
                      {b.pending_actions > 0 ? <span className="inline-block bg-mustard/25 text-earth font-600 px-2 py-0.5 rounded-full text-xs">{b.pending_actions} pending</span> : <span className="text-brown/40">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button title="Show/reset login" onClick={() => regen(b)} data-testid={`brand-regen-${b.id}`} className="w-8 h-8 rounded-full border border-sage flex items-center justify-center hover:bg-sage/10"><KeyRound className="w-4 h-4 text-olive" /></button>
                        <button title="Toggle status" onClick={() => toggleStatus(b)} data-testid={`brand-toggle-${b.id}`} className="w-8 h-8 rounded-full border border-sage flex items-center justify-center hover:bg-sage/10"><Power className="w-4 h-4 text-olive" /></button>
                        <button title="Edit" onClick={() => setEditing({ ...BLANK, ...b })} data-testid={`brand-edit-${b.id}`} className="w-8 h-8 rounded-full border border-sage flex items-center justify-center hover:bg-sage/10"><Pencil className="w-4 h-4 text-forest" /></button>
                        <button title="Delete" onClick={() => del(b)} data-testid={`brand-delete-${b.id}`} className="w-8 h-8 rounded-full border border-sage flex items-center justify-center hover:bg-sage/10"><Trash2 className="w-4 h-4 text-terracotta" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-cream max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-forest">{editing?.id ? "Edit Brand" : "Add Brand"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-sm mb-1 block">Brand Name *</Label><Input value={editing.brand_name} onChange={(e) => setEditing({ ...editing, brand_name: e.target.value })} className="rounded-xl bg-white" data-testid="bf-brand-name" /></div>
                <div><Label className="text-sm mb-1 block">Legal Name</Label><Input value={editing.legal_name} onChange={(e) => setEditing({ ...editing, legal_name: e.target.value })} className="rounded-xl bg-white" /></div>
              </div>
              <div><Label className="text-sm mb-1.5 block">Brand Logo</Label><ImageUploader value={editing.logo} onChange={(url) => setEditing((c) => ({ ...c, logo: url }))} label="Upload brand logo" /></div>
              <div><Label className="text-sm mb-1 block">Description</Label><Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="rounded-xl bg-white" rows={3} /></div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-sm mb-1 block">Website</Label><Input value={editing.website} onChange={(e) => setEditing({ ...editing, website: e.target.value })} placeholder="https://" className="rounded-xl bg-white" /></div>
                <div><Label className="text-sm mb-1 block">Contact Person</Label><Input value={editing.contact_person} onChange={(e) => setEditing({ ...editing, contact_person: e.target.value })} className="rounded-xl bg-white" /></div>
                <div><Label className="text-sm mb-1 block">Contact Email</Label><Input value={editing.contact_email} onChange={(e) => setEditing({ ...editing, contact_email: e.target.value })} className="rounded-xl bg-white" data-testid="bf-contact-email" /></div>
                <div><Label className="text-sm mb-1 block">Contact Phone</Label><Input value={editing.contact_phone} onChange={(e) => setEditing({ ...editing, contact_phone: e.target.value })} className="rounded-xl bg-white" /></div>
              </div>
              <div><Label className="text-sm mb-1 block">Address</Label><Textarea value={editing.address} onChange={(e) => setEditing({ ...editing, address: e.target.value })} className="rounded-xl bg-white" rows={2} /></div>

              <div><Label className="text-sm mb-1 block">Operating Cities</Label><TagInput value={editing.operating_cities} onChange={(v) => setEditing({ ...editing, operating_cities: v })} placeholder="Type a city, press Enter" testid="bf-city" /></div>

              <div>
                <Label className="text-sm mb-1.5 block">Product Genres <span className="text-brown/50 font-400">(from your store categories)</span></Label>
                <div className="flex flex-wrap gap-1.5">
                  {cats.map((c) => (
                    <button type="button" key={c.id} onClick={() => toggleGenre(c.name)} data-testid={`bf-genre-${c.id}`}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${editing.product_genres.includes(c.name) ? "bg-forest text-cream border-forest" : "bg-white text-brown border-sage/50 hover:border-forest"}`}>{c.name}</button>
                  ))}
                  {cats.length === 0 && <span className="text-xs text-brown/50">No categories found.</span>}
                </div>
              </div>
              <div><Label className="text-sm mb-1 block">Product Types <span className="text-brown/50 font-400">(free-text tags)</span></Label><TagInput value={editing.product_types} onChange={(v) => setEditing({ ...editing, product_types: v })} placeholder="e.g. Cold-Pressed Oil, press Enter" testid="bf-type" /></div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div><Label className="text-sm mb-1 block">GST</Label><Input value={editing.gst} onChange={(e) => setEditing({ ...editing, gst: e.target.value })} className="rounded-xl bg-white" /></div>
                <div><Label className="text-sm mb-1 block">PAN</Label><Input value={editing.pan} onChange={(e) => setEditing({ ...editing, pan: e.target.value })} className="rounded-xl bg-white" /></div>
                <div><Label className="text-sm mb-1 block">FSSAI No.</Label><Input value={editing.fssai} onChange={(e) => setEditing({ ...editing, fssai: e.target.value })} className="rounded-xl bg-white" /></div>
              </div>
              <div><Label className="text-sm mb-1 block">Licenses</Label><TagInput value={editing.licenses} onChange={(v) => setEditing({ ...editing, licenses: v })} placeholder="License / registration no., press Enter" /></div>
              <div><Label className="text-sm mb-1 block">Certifications</Label><TagInput value={editing.certifications} onChange={(v) => setEditing({ ...editing, certifications: v })} placeholder="e.g. USDA Organic, press Enter" /></div>

              <div className="bg-sand rounded-xl p-3">
                <Label className="text-sm mb-1 block">Portal Login Email <span className="text-brown/50 font-400">(defaults to contact email)</span></Label>
                <Input value={editing.portal_email} onChange={(e) => setEditing({ ...editing, portal_email: e.target.value })} placeholder="brand@company.com" className="rounded-xl bg-white" data-testid="bf-portal-email" />
                {!editing.id && <p className="text-xs text-brown/60 mt-1">A brand portal login with a temporary password will be generated on save.</p>}
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={save} disabled={saving} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="save-brand-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing?.id ? "Save Changes" : "Create Brand & Generate Login")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials dialog */}
      <Dialog open={!!creds} onOpenChange={(o) => !o && setCreds(null)}>
        <DialogContent className="bg-cream max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-forest">{creds?.brand_name} — Portal Access</DialogTitle></DialogHeader>
          {creds && <CredentialsBox creds={creds} />}
          <DialogFooter>
            <a href={creds?.login_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-olive hover:text-forest mr-auto"><ExternalLink className="w-4 h-4" /> Open portal</a>
            <Button onClick={() => setCreds(null)} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="creds-done-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
